import type { FiscalFamilyId } from "@backchannel/tax-contracts";
import { UFS } from "./facets";
import { familyGeography } from "./family-geography";

const NCM_CODE = /(?<!\d)(\d{4}\.?\d{2}\.?\d{2})(?!\d)/;
const UF_NAMES: Record<string, string> = {
  acre: "AC",
  alagoas: "AL",
  amapa: "AP",
  amazonas: "AM",
  bahia: "BA",
  ceara: "CE",
  "distrito federal": "DF",
  brasilia: "DF",
  espirito: "ES",
  goias: "GO",
  maranhao: "MA",
  "mato grosso do sul": "MS",
  "mato grosso": "MT",
  "minas gerais": "MG",
  minas: "MG",
  paraiba: "PB",
  parana: "PR",
  pernambuco: "PE",
  piaui: "PI",
  "rio de janeiro": "RJ",
  "rio grande do norte": "RN",
  "rio grande do sul": "RS",
  rondonia: "RO",
  roraima: "RR",
  "santa catarina": "SC",
  "sao paulo": "SP",
  sergipe: "SE",
  tocantins: "TO",
};

const FAMILY_ALIASES: Array<{ family: FiscalFamilyId; pattern: RegExp }> = [
  { family: "ICMS_ST", pattern: /\bicms[\s-]?st\b|\bsubstituicao\b|\bst\b/ },
  { family: "MVA", pattern: /\bmva\b|\biva[\s-]?st\b/ },
  { family: "PIS_COFINS", pattern: /\bpis\b|\bcofins\b/ },
  { family: "ISS", pattern: /\biss\b/ },
  { family: "IPI", pattern: /\bipi\b|\btipi\b/ },
  { family: "CEST", pattern: /\bcest\b/ },
  { family: "NCM", pattern: /\bncm\b|\bclassif\b|\bsiscomex\b/ },
  { family: "ICMS", pattern: /\bicms\b|\bricms\b|\bdifal\b/ },
];

export type DiscoverySource = {
  id: string;
  name: string;
  publisher: string;
  jurisdiction: string;
  status: string;
};

export type DiscoveryIntent =
  | { kind: "empty" }
  | {
      kind: "excluded";
      query: string;
      family: "PIS_COFINS" | "ISS";
      ufs: string[];
      ncm: string | null;
    }
  | {
      kind: "ready";
      query: string;
      family: FiscalFamilyId | null;
      ufs: string[];
      ncm: string | null;
    };

export const DISCOVERY_MAX_DOCUMENTS = 8;
export type DiscoveryLocale = "pt-BR" | "es" | "en";

export function foldQuery(value: string): string {
  return value.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();
}

function uniqueCodes(values: string[]): string[] {
  return [...new Set(values)];
}

export function parseDiscoveryQuery(raw: string): DiscoveryIntent {
  const query = raw.trim();
  if (!query) return { kind: "empty" };

  const folded = foldQuery(query);
  const ncmMatch = query.match(NCM_CODE);
  const ncm = ncmMatch?.[1]?.replaceAll(".", "") ?? null;
  const compactNcm = ncm && ncm.length === 8 ? ncm : null;

  // Punctuation and route arrows delimit a UF just like whitespace. Letters
  // and digits do not: "para" must never be mistaken for the code PA.
  const boundary = (phrase: string) =>
    new RegExp(`(?<![\\p{L}\\p{N}])${phrase}(?![\\p{L}\\p{N}])`, "u");
  const codeMatches = UFS.filter((uf) => boundary(uf.toLowerCase()).test(folded));
  const namedCodes: string[] = [];
  let remainingNames = folded;
  // Consume the longest state names first so Mato Grosso do Sul does not
  // also add Mato Grosso. Word boundaries exclude partial words/other states.
  for (const [name, uf] of Object.entries(UF_NAMES).sort(([a], [b]) => b.length - a.length)) {
    const pattern = boundary(name.replaceAll(" ", "\\s+"));
    if (pattern.test(remainingNames)) {
      namedCodes.push(uf);
      remainingNames = remainingNames.replace(new RegExp(pattern.source, "gu"), " ");
    }
  }
  // Accent folding conflates Pará with the Portuguese preposition "para".
  // Retain explicit accented names or an unambiguous state-name qualifier.
  if (
    boundary("pará").test(query.normalize("NFC").toLocaleLowerCase()) ||
    boundary("estado\\s+do\\s+para").test(folded)
  )
    namedCodes.push("PA");
  const ufs = uniqueCodes([...codeMatches, ...namedCodes]);

  const family = FAMILY_ALIASES.find(({ pattern }) => pattern.test(folded))?.family ?? null;
  if (family === "PIS_COFINS" || family === "ISS") {
    return { kind: "excluded", query, family, ufs, ncm: compactNcm };
  }

  // Open-ended fiscal questions are valid agent work. Keep very short input
  // empty so the desk does not dispatch noise while the operator is typing.
  if (!family && !compactNcm && ufs.length === 0 && query.length < 3) {
    return { kind: "empty" };
  }

  return { kind: "ready", query, family, ufs, ncm: compactNcm };
}

function sourceLooksFederal(source: DiscoverySource): boolean {
  if (source.jurisdiction === "BR-Federal") return true;
  const haystack = foldQuery(`${source.name} ${source.publisher}`);
  return (
    haystack.includes("confaz") ||
    haystack.includes("receita federal") ||
    haystack.includes("siscomex") ||
    haystack.includes("classif")
  );
}

function sourceMatchesUf(source: DiscoverySource, uf: string): boolean {
  return (
    source.jurisdiction === `BR-${uf}` ||
    foldQuery(source.name).includes(`sefaz-${uf.toLowerCase()}`)
  );
}

function sourceMatchesFamily(source: DiscoverySource, family: FiscalFamilyId): boolean {
  const haystack = foldQuery(`${source.name} ${source.publisher}`);
  switch (family) {
    case "NCM":
      return (
        haystack.includes("ncm") || haystack.includes("classif") || haystack.includes("siscomex")
      );
    case "IPI":
      return haystack.includes("tipi") || haystack.includes("ipi") || haystack.includes("classif");
    case "CEST":
    case "ICMS":
    case "ICMS_ST":
    case "MVA":
      return haystack.includes("confaz") || haystack.includes("sefaz") || haystack.includes("icms");
    case "PIS_COFINS":
    case "ISS":
      return false;
    default: {
      const _exhaustive: never = family;
      return _exhaustive;
    }
  }
}

function isDiscoveryResearchStatus(status: string): boolean {
  return status === "ACTIVE" || status === "DEGRADED";
}

/**
 * Cap matched sources while keeping UF-specific hits that the question named.
 * ACTIVE stays ahead of DEGRADED; within each group the filter order is kept.
 */
function takeDiscoverySources(
  eligible: readonly DiscoverySource[],
  intentUfs: readonly string[],
  cap: number,
): DiscoverySource[] {
  const active = eligible.filter((source) => source.status === "ACTIVE");
  const degraded = eligible.filter((source) => source.status === "DEGRADED");
  const ordered = [...active, ...degraded];
  if (intentUfs.length === 0 || ordered.length <= cap) {
    return ordered.slice(0, cap);
  }

  // One source per UF the question names comes first, so a PR -> SP question
  // keeps SEFAZ-PR even when SP alone has more sources than the cap; then one
  // federal bridge (CONFAZ); then the remaining sources in order.
  const selected = new Set<DiscoverySource>();
  for (const uf of intentUfs) {
    const first = ordered.find((source) => !selected.has(source) && sourceMatchesUf(source, uf));
    if (first && selected.size < cap) selected.add(first);
  }
  const federal = ordered.find((source) => !selected.has(source) && sourceLooksFederal(source));
  if (federal && selected.size < cap) selected.add(federal);
  for (const source of ordered) {
    if (selected.size >= cap) break;
    selected.add(source);
  }
  return ordered.filter((source) => selected.has(source));
}

export function matchDiscoverySources(
  intent: DiscoveryIntent,
  sources: readonly DiscoverySource[],
): DiscoverySource[] {
  if (intent.kind !== "ready") return [];

  const geography = intent.family ? familyGeography(intent.family) : null;
  const matched = sources.filter((source) => {
    if (!isDiscoveryResearchStatus(source.status)) return false;
    if (intent.family && !sourceMatchesFamily(source, intent.family)) {
      if (intent.ncm && sourceMatchesFamily(source, "NCM")) {
        return isDiscoveryResearchStatus(source.status);
      }
      return false;
    }
    if (intent.ufs.length > 0) {
      const territorial = intent.ufs.some((uf) => sourceMatchesUf(source, uf));
      const federalBridge =
        (geography === "convenio" || geography === "state_watched" || geography === "federal") &&
        sourceLooksFederal(source);
      return territorial || federalBridge;
    }
    if (intent.ncm) return sourceMatchesFamily(source, intent.family ?? "NCM");
    return intent.family ? sourceMatchesFamily(source, intent.family) : sourceLooksFederal(source);
  });

  return takeDiscoverySources(matched, intent.ufs, 4);
}

export function discoveryReasonDraft(
  intent: DiscoveryIntent,
  locale: DiscoveryLocale = "pt-BR",
): string {
  if (intent.kind === "empty") return "";
  const sourcePhrase = {
    "pt-BR": "conferir o ato oficial na fonte oficial autorizada",
    es: "verificar el acto oficial en la fuente oficial autorizada",
    en: "verify the official act at the authorized official source",
  }[locale];
  return `${intent.query} — ${sourcePhrase}.`;
}

const STOP_WORDS = new Set(["de", "da", "do", "das", "dos", "e", "a", "o", "em", "para", "com"]);

const FAMILY_WORDS =
  /\b(icms-?st|icms|mva|iva-?st|ipi|tipi|cest|ncm|ricms|difal|pis|cofins|iss|substituicao|classif|siscomex)\b/g;

export function discoveryTopicTokens(intent: DiscoveryIntent): string[] {
  if (intent.kind === "empty") return [];

  let rest = foldQuery(intent.query);
  for (const name of Object.keys(UF_NAMES).sort((left, right) => right.length - left.length)) {
    rest = rest.replaceAll(name, " ");
  }
  for (const uf of intent.ufs) {
    rest = rest.replace(new RegExp(`\\b${uf.toLowerCase()}\\b`, "g"), " ");
  }
  if (intent.ncm) rest = rest.replaceAll(intent.ncm, " ");
  rest = rest.replace(FAMILY_WORDS, " ");

  return uniqueCodes(
    rest
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length >= 3 && !STOP_WORDS.has(token) && !/^\d+$/.test(token)),
  );
}

export function evidenceMatchesDiscovery(
  intent: DiscoveryIntent,
  item: {
    title: string;
    publisher: string;
    jurisdiction: string;
    taxTags: readonly string[];
    sourceName?: string;
  },
): boolean {
  if (intent.kind !== "ready") return false;

  const haystack = foldQuery(
    `${item.title} ${item.publisher} ${item.jurisdiction} ${item.taxTags.join(" ")} ${item.sourceName ?? ""}`,
  );
  const topics = discoveryTopicTokens(intent);
  if (topics.length > 0) return topics.every((token) => haystack.includes(token));

  const ufOk =
    intent.ufs.length === 0 ||
    intent.ufs.some((uf) => item.jurisdiction === `BR-${uf}` || item.jurisdiction === "BR-Federal");
  const familyNeedle = intent.family
    ? foldQuery(intent.family.replace("_ST", "").replaceAll("_", " "))
    : null;
  const familyOk =
    !familyNeedle ||
    haystack.includes(familyNeedle.split(" ")[0] ?? familyNeedle) ||
    item.taxTags.some((tag) => foldQuery(tag).includes(familyNeedle.replaceAll(" ", "-")));
  const ncmOk = !intent.ncm || haystack.includes(intent.ncm);
  return ufOk && familyOk && ncmOk;
}
